
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, type LucideIcon } from 'lucide-react';
import { ticketsApi } from '@/shared/api/tickets';
import { Scroller, Select, Skeleton, Tooltip } from '@/shared/ui';
import { formatWorkMinutes, type TicketEvent } from '@/entities/ticket';
import { EVENT_ICONS, ticketStatusLabel } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import styles from './TicketHistory.module.css';

const PAGE_SIZE = 30;

interface EventMeta {
  label: string;
  tone?: 'ok' | 'warn' | 'danger' | 'info';
}

const EVENT_META: Record<string, EventMeta> = {
  ticket_created: { label: 'Заявка создана', tone: 'ok' },
  message_sent: { label: 'Сообщение в чате' },
  message_edited: { label: 'Сообщение изменено' },
  message_deleted: { label: 'Сообщение удалено', tone: 'warn' },
  participant_added: { label: 'Добавлен участник' },
  participant_removed: { label: 'Участник убран' },
  assignee_added: { label: 'Назначен исполнитель', tone: 'info' },
  assignee_removed: { label: 'Исполнитель снят' },
  assignee_reassigned: { label: 'Исполнитель переназначен', tone: 'info' },
  group_assigned: { label: 'Назначена группе', tone: 'info' },
  group_reassigned: { label: 'Передана другой группе', tone: 'info' },
  status_changed: { label: 'Статус изменён', tone: 'info' },
  approval_created: { label: 'Запрошено согласование' },
  approval_approved: { label: 'Согласование одобрено', tone: 'ok' },
  approval_rejected: { label: 'Согласование отклонено', tone: 'danger' },
  approval_removed: { label: 'Согласование удалено' },
  approval_cancelled: { label: 'Согласования сняты' },
  approval_round_started: { label: 'Отправлена комитету на согласование', tone: 'info' },
  approval_round_approved: { label: 'Комитет согласовал', tone: 'ok' },
  approval_round_rejected: { label: 'Комитет не согласовал', tone: 'danger' },
  approval_round_removed: { label: 'Согласование комитета удалено' },
  approval_level_reminded: { label: 'Напоминание согласующим', tone: 'warn' },
  ticket_viewed: { label: 'Просмотр заявки' },
  template_applied: { label: 'Применён шаблон' },
  mass_incident_marked: { label: 'Помечена как массовый сбой', tone: 'warn' },
  mass_incident_unmarked: { label: 'Снята пометка массового сбоя' },
  workflow_notify: { label: 'Уведомление по флоу' },
  workflow_chain: { label: 'Автоматизация флоу' },
  complexity_changed: { label: 'Сложность изменена', tone: 'info' },
  reclassified: { label: 'Категория изменена', tone: 'info' },
  plan_changed: { label: 'План изменён', tone: 'info' },
  complexity_requested: { label: 'Запрошена смена сложности', tone: 'warn' },
  complexity_request_decided: { label: 'Решение по сложности' },
  resolution_confirmed: { label: 'Выполнение подтверждено', tone: 'ok' },
  resolution_rejected: { label: 'Выполнение не подтверждено', tone: 'danger' },
  ticket_rated: { label: 'Оценка исполнения', tone: 'info' },
  confirmation_reminded: { label: 'Напоминание о подтверждении', tone: 'warn' },
  message_moved: { label: 'Сообщение перенесено', tone: 'info' },
  email_sent: { label: 'Сообщение отправлено письмом', tone: 'info' },
};

const FALLBACK_META: EventMeta = { label: 'Событие' };
const FALLBACK_ICON: LucideIcon = History;

const FILTER_OPTIONS = [
  { value: '', label: 'Все события' },
  ...Object.entries(EVENT_META).map(([value, m]) => ({ value, label: m.label })),
];

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function eventDetails(e: TicketEvent): string | null {
  const p = e.payload ?? {};
  const str = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : null);
  switch (e.eventType) {
    case 'ticket_created':
      return str('subject');
    case 'status_changed': {
      const from = str('oldStatus');
      const to = str('newStatus');
      if (!from && !to) return null;
      const via = str('via');
      return `${ticketStatusLabel(from ?? '')} → ${ticketStatusLabel(to ?? '')}${
        via === 'trigger' || p.trigger ? ' (автопереход)' : ''
      }`;
    }
    case 'reclassified': {
      const side = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
      const label = (v: Record<string, unknown>) =>
        [v.category, v.subcategory].filter((x): x is string => typeof x === 'string' && !!x).join(' › ') ||
        'без категории';
      return `${label(side(p.from))} → ${label(side(p.to))}`;
    }
    case 'complexity_changed': {
      const o = p.oldClass != null ? `класс ${p.oldClass}` : null;
      const n = p.newClass != null ? `класс ${p.newClass}` : null;
      const base = [o, n].filter(Boolean).join(' → ');
      return [base, str('comment') && `«${str('comment')}»`].filter(Boolean).join(' ');
    }
    case 'plan_changed': {
      const side = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
      const from = side(p.from);
      const to = side(p.to);
      const hours = (v: unknown) => (typeof v === 'number' ? formatWorkMinutes(v) : 'без часов');
      const day = (v: unknown) => (typeof v === 'string' ? `к ${v.split('-').reverse().join('.')}` : 'без даты');
      const parts: string[] = [];
      if (from.workMinutes !== to.workMinutes) parts.push(`${hours(from.workMinutes)} → ${hours(to.workMinutes)}`);
      if (from.dueOn !== to.dueOn) parts.push(`${day(from.dueOn)} → ${day(to.dueOn)}`);
      return parts.join(' · ') || null;
    }
    case 'complexity_requested': {
      const base = p.requestedClass != null ? `класс ${p.currentClass ?? '?'} → ${p.requestedClass}` : null;
      return [base, str('comment') && `«${str('comment')}»`].filter(Boolean).join(' ');
    }
    case 'complexity_request_decided':
      return p.approved === true
        ? `одобрено, класс ${p.resultClass ?? '?'}`
        : `отклонено, остался класс ${p.resultClass ?? '?'}`;
    case 'group_assigned':
    case 'group_reassigned': {
      const from = str('fromGroupName');
      const to = str('toGroupName');
      if (!from && !to) return null;
      return from ? `${from} → ${to ?? 'без группы'}` : (to ?? null);
    }
    case 'approval_created':
      return str('subject');
    case 'approval_cancelled': {
      const reason = str('reason');
      const count = typeof p.count === 'number' ? `${p.count} шт.` : null;
      return [count, reason && `заявка ушла в «${ticketStatusLabel(reason)}»`].filter(Boolean).join(' — ') || null;
    }
    case 'approval_approved':
    case 'approval_rejected': {
      const subj = str('subject');
      const comment = str('comment');
      return [subj, comment && `«${comment}»`].filter(Boolean).join(' — ') || null;
    }
    case 'workflow_chain': {
      const blocks = Array.isArray(p.blocks) ? p.blocks.length : null;
      return [str('transition'), blocks != null ? `блоков: ${blocks}` : null]
        .filter(Boolean)
        .join(', ');
    }
    case 'message_moved': {
      if (p.toNumber != null) return `в заявку №${p.toNumber}`;
      if (p.fromNumber != null) return `из заявки №${p.fromNumber}`;
      return null;
    }
    case 'email_sent': {
      const to = Array.isArray(p.to) ? (p.to as string[]) : [];
      const cc = Array.isArray(p.cc) ? (p.cc as string[]) : [];
      return [to.join(', '), cc.length ? `копия: ${cc.join(', ')}` : null].filter(Boolean).join(' · ') || null;
    }
    case 'ticket_viewed': {
      return [str('browser'), str('os'), str('ip')].filter(Boolean).join(' · ') || null;
    }
    default: {
      const skip = new Set([
        'userId',
        'approverId',
        'eventId',
        'assignedGroupId',
        'fromUserId',
        'toUserId',
        'fromGroupId',
        'toGroupId',
        'fromGroupName',
        'toGroupName',
      ]);
      const parts = Object.entries(p)
        .filter(([k, v]) => !skip.has(k) && v != null && typeof v !== 'object')
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${String(v)}`);
      return parts.length ? parts.join(', ') : null;
    }
  }
}

export interface TicketHistoryProps {
  ticketId: string;
  bare?: boolean;
  filterSlot?: HTMLElement | null;
}

export const TicketHistory: React.FC<TicketHistoryProps> = ({ ticketId, bare = false, filterSlot }) => {
  const [events, setEvents] = useState<TicketEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const isOpen = bare || open;

  const requestSeq = useRef(0);

  const load = useCallback(
    async (offset: number, append: boolean) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      try {
        const res = await ticketsApi.getHistory(ticketId, {
          limit: PAGE_SIZE,
          offset,
          ...(typeFilter ? { eventType: typeFilter } : {}),
        });
        if (seq !== requestSeq.current) return;
        setTotal(res.total);
        setIsFiltered(res.isFiltered);
        setEvents((prev) => (append ? [...prev, ...res.events] : res.events));
      } catch {
        if (seq === requestSeq.current && !append) setEvents([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [ticketId, typeFilter],
  );

  useEffect(() => {
    void load(0, false);
  }, [load]);

  const remaining = total - events.length;

  const selectEl = !isFiltered ? (
    <Select
      aria-label="Тип события"
      value={typeFilter}
      onValueChange={setTypeFilter}
      options={FILTER_OPTIONS}
      className={styles.filterSelect}
    />
  ) : null;

  const body = isOpen && (
        <Scroller className={cn(styles.body, bare && styles.bodyBare)}>
          {bare && isFiltered && (
            <Tooltip content="Полный журнал доступен сотрудникам поддержки">
              <span className={styles.filteredBadge}>основные события</span>
            </Tooltip>
          )}
          { }
          {selectEl && !filterSlot && <div className={styles.toolbar}>{selectEl}</div>}

          {loading && events.length === 0 ? (
            <ol className={styles.timeline} aria-busy>
              {Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className={styles.item}>
                  <Skeleton circle width={26} height={26} className={styles.skelIcon} />
                  <div className={styles.content}>
                    <Skeleton width={`${60 - i * 6}%`} height={13} radius={6} />
                    <Skeleton width="40%" height={11} radius={6} className={styles.skelSub} />
                  </div>
                </li>
              ))}
            </ol>
          ) : events.length === 0 ? (
            <p className={styles.muted}>Событий нет</p>
          ) : (
            <ol className={styles.timeline}>
              {events.map((e) => {
                const meta = EVENT_META[e.eventType] ?? FALLBACK_META;
                const Icon = EVENT_ICONS[e.eventType] ?? FALLBACK_ICON;
                const details = eventDetails(e);
                return (
                  <li key={e.id} className={styles.item}>
                    <span className={cn(styles.dot, meta.tone && styles[`tone-${meta.tone}`])}>
                      <Icon size={13} aria-hidden />
                    </span>
                    <div className={styles.content}>
                      <div className={styles.titleRow}>
                        <span className={styles.title}>{meta.label}</span>
                        <time className={styles.time} dateTime={e.createdAt}>
                          {fmtDateTime(e.createdAt)}
                        </time>
                      </div>
                      {(details || e.actor) && (
                        <div className={styles.details}>
                          {e.actor?.displayName && (
                            <span className={styles.actor}>{e.actor.displayName}</span>
                          )}
                          {details && <span>{details}</span>}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {remaining > 0 && (
            <button
              type="button"
              className={styles.more}
              disabled={loading}
              onClick={() => load(events.length, true)}
            >
              {loading ? 'Загрузка…' : `Показать ещё (${remaining})`}
            </button>
          )}
        </Scroller>
      );

  if (bare)
    return (
      <>
        {body}
        {filterSlot && selectEl ? createPortal(selectEl, filterSlot) : null}
      </>
    );

  return (
    <section className={styles.card}>
      <button type="button" className={styles.head} onClick={() => setOpen((v) => !v)}>
        <span className={styles.headTitle}>
          <History size={15} aria-hidden />
          История
          <span className={styles.count}>{total}</span>
        </span>
        <span className={styles.headRight}>
          {isFiltered && (
            <Tooltip content="Полный журнал доступен сотрудникам поддержки">
              <span className={styles.filteredBadge}>основные события</span>
            </Tooltip>
          )}
          <span className={cn(styles.chevron, open && styles.chevronOpen)} aria-hidden>
            ▾
          </span>
        </span>
      </button>

      {body}
    </section>
  );
};
